import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import communityAPI from '../../../api/community';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import Card from '../../../componentes/ui/Card';

const STORAGE_KEY = 'community:emailChange';

const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();

export default function VerifyEmailChangeOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useCommunityAuth();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const inputsRef = useRef([]);

  const statePayload = location.state || {};

  const fallbackPayload = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const username = statePayload.username || fallbackPayload?.username || user?.username || '';
  const newEmail = normalizeEmail(statePayload.newEmail || fallbackPayload?.newEmail || '');

  useEffect(() => {
    if (!user) {
      message.error('Please login to verify your email change.');
      navigate('/community/login');
      return;
    }

    if (!newEmail) {
      message.error('Missing email change request details.');
      navigate('/community/profile/edit');
      return;
    }

    if (username && user.username && username !== user.username) {
      message.error('This verification request does not match the current account.');
      navigate('/community/profile/edit');
    }
  }, [user, username, newEmail, navigate]);

  const handleOtpChange = (index, value) => {
    if (!value) {
      const next = [...otp];
      next[index] = '';
      setOtp(next);
      return;
    }

    const digit = value.replace(/\D/g, '').slice(-1);
    if (!digit) return;

    const next = [...otp];
    next[index] = digit;
    setOtp(next);

    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const verify = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      message.error('Please enter the 6-digit code.');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await communityAPI.users.verifyEmailChange(username, newEmail, otpCode);
      const userData = response.data?.user || response.data;
      login(userData);
      sessionStorage.removeItem(STORAGE_KEY);
      message.success(response.data?.detail || 'Email updated successfully.');
      navigate('/community/profile');
    } catch (err) {
      const apiMessage = err.response?.data?.detail || err.response?.data?.otp || err.response?.data?.new_email;
      message.error(apiMessage || 'Verification failed.');
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const resend = async () => {
    if (!newEmail) return;
    setIsResending(true);
    try {
      await communityAPI.users.resendEmailChangeOTP(username, newEmail);
      message.success('A new code has been sent.');
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } catch (err) {
      const apiMessage = err.response?.data?.detail || err.response?.data?.new_email;
      message.error(apiMessage || 'Unable to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 pt-24 pb-12">
      <Card className="max-w-md w-full p-8 shadow-lg border-0">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verify New Email</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Enter the 6-digit code sent to <span className="font-semibold">{newEmail}</span>.
        </p>

        <div className="flex justify-between gap-2 mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputsRef.current[index] = el; }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={isVerifying}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={verify}
          disabled={isVerifying || otp.join('').length !== 6}
          className="w-full px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-[#229bb8] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isVerifying ? 'Verifying…' : 'Verify Email'}
        </button>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={resend}
            disabled={isResending || isVerifying}
            className="text-primary hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isResending ? 'Sending…' : 'Resend code'}
          </button>

          <Link to="/community/profile/edit" className="text-gray-600 dark:text-gray-300 hover:underline">
            Back to edit profile
          </Link>
        </div>
      </Card>
    </div>
  );
}
